import React, { useState, useEffect } from 'react';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import ChatWidget from '../home/components/ChatWidget';

const AIConsultingPage: React.FC = () => {
  const [maturityScore, setMaturityScore] = useState(45);
  const [dataReadiness, setDataReadiness] = useState(5);
  const [aiSkills, setAiSkills] = useState(3);
  const [techInfrastructure, setTechInfrastructure] = useState(6);
  const [orgReadiness, setOrgReadiness] = useState(4);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [charCount, setCharCount] = useState(0);

  useEffect(() => {
    const score = Math.round((dataReadiness + aiSkills + techInfrastructure + orgReadiness) / 4 * 10);
    setMaturityScore(score);
  }, [dataReadiness, aiSkills, techInfrastructure, orgReadiness]);

  const getMaturityRecommendation = () => {
    if (maturityScore < 30) {
      return "Your organization is in the early stages of AI readiness. Focus on building foundational data infrastructure and developing AI skills.";
    } else if (maturityScore < 60) {
      return "Your organization shows moderate AI readiness. Focus on data infrastructure and skills development.";
    } else if (maturityScore < 80) {
      return "Your organization has good AI readiness. Consider pilot implementations and scaling successful use cases.";
    } else {
      return "Your organization demonstrates strong AI maturity. Focus on optimization and advanced AI implementations.";
    }
  };

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    
    // Get all checked use cases
    const useCases = Array.from(formData.getAll('use_cases')).join(', ');
    
    // Create URL-encoded data
    const data = new URLSearchParams();
    data.append('company_name', formData.get('company_name') as string);
    data.append('industry', formData.get('industry') as string);
    data.append('contact_name', formData.get('contact_name') as string);
    data.append('job_title', formData.get('job_title') as string);
    data.append('email', formData.get('email') as string);
    data.append('phone', formData.get('phone') as string);
    data.append('company_size', formData.get('company_size') as string);
    data.append('ai_budget', formData.get('ai_budget') as string);
    data.append('use_cases', useCases);
    data.append('ai_requirements', formData.get('ai_requirements') as string);
    data.append('preferred_timeline', formData.get('preferred_timeline') as string);

    try {
      const response = await fetch('https://readdy.ai/api/form/d4vuqf2mtigcascn5qbg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: data.toString()
      });

      if (response.ok) {
        setShowSuccessModal(true);
        form.reset();
        setCharCount(0);
      }
    } catch (error) {
      console.error('Form submission error:', error);
    }
  };

  const scrollToAssessmentTools = () => {
    document.getElementById('assessment-tools')?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToConsultationForm = () => {
    document.getElementById('consultation-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative w-full bg-gradient-to-r from-blue-900 via-blue-800 to-blue-900 py-20">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://public.readdy.ai/ai/img_res/32289a4caaf232b2c535b36bffd90276.jpg)',
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/80 to-transparent"></div>
        <div className="container mx-auto px-4 py-20 relative">
          <div className="w-full max-w-2xl">
            <div className="mb-4">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-50 text-purple-600">
                <div className="w-4 h-4 flex items-center justify-center mr-2">
                  <i className="ri-robot-line"></i>
                </div>
                AI Consulting
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Enterprise AI Consulting &<br />Implementation Services
            </h1>
            <p className="text-lg text-gray-700 mb-8">
              Transform your business with strategic AI consulting, implementation services, and machine learning solutions. Our expert team guides you through AI adoption, governance frameworks, and successful digital transformation.
            </p>
            <div className="flex flex-wrap gap-4">
              <button 
                onClick={scrollToConsultationForm}
                className="bg-primary text-white px-6 py-3 rounded-lg whitespace-nowrap font-medium inline-flex items-center hover:bg-primary/90 transition-colors cursor-pointer"
              >
                <div className="w-5 h-5 flex items-center justify-center mr-2">
                  <i className="ri-robot-line"></i>
                </div>
                Schedule AI Consultation
              </button>
              <button 
                onClick={scrollToAssessmentTools}
                className="border border-gray-300 text-gray-700 px-6 py-3 rounded-lg whitespace-nowrap font-medium inline-flex items-center hover:bg-gray-50 transition-colors cursor-pointer"
              >
                <div className="w-5 h-5 flex items-center justify-center mr-2">
                  <i className="ri-bar-chart-line"></i>
                </div>
                Free AI Readiness Assessment
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Core AI Consulting Services */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Core AI Consulting Services</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Comprehensive AI consulting solutions designed to accelerate your artificial intelligence adoption and maximize business value through strategic implementation.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-8">
              <div className="w-16 h-16 bg-blue-50 rounded-lg flex items-center justify-center mb-6">
                <div className="w-8 h-8 flex items-center justify-center text-primary">
                  <i className="ri-lightbulb-line text-3xl"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-4">AI Strategy Consulting</h3>
              <p className="text-gray-600 mb-6">
                Strategic AI roadmap development, business case analysis, and technology selection to align AI initiatives with your business objectives.
              </p>
              <ul className="space-y-2 mb-6">
                <li className="flex items-start">
                  <div className="w-4 h-4 flex items-center justify-center text-primary mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-sm text-gray-700">AI maturity assessment</span>
                </li>
                <li className="flex items-start">
                  <div className="w-4 h-4 flex items-center justify-center text-primary mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-sm text-gray-700">Strategic roadmap creation</span>
                </li>
                <li className="flex items-start">
                  <div className="w-4 h-4 flex items-center justify-center text-primary mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-sm text-gray-700">ROI analysis & business case</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-8">
              <div className="w-16 h-16 bg-green-50 rounded-lg flex items-center justify-center mb-6">
                <div className="w-8 h-8 flex items-center justify-center text-green-600">
                  <i className="ri-settings-3-line text-3xl"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-4">Implementation Services</h3>
              <p className="text-gray-600 mb-6">
                End-to-end AI solution implementation, system integration, and deployment services with ongoing support and optimization.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <div className="w-4 h-4 flex items-center justify-center text-green-600 mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-sm text-gray-700">Custom AI development</span>
                </li>
                <li className="flex items-start">
                  <div className="w-4 h-4 flex items-center justify-center text-green-600 mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-sm text-gray-700">System integration</span>
                </li>
                <li className="flex items-start">
                  <div className="w-4 h-4 flex items-center justify-center text-green-600 mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-sm text-gray-700">Performance optimization</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-8">
              <div className="w-16 h-16 bg-purple-50 rounded-lg flex items-center justify-center mb-6">
                <div className="w-8 h-8 flex items-center justify-center text-purple-600">
                  <i className="ri-bar-chart-line text-3xl"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-4">AI Readiness Assessment</h3>
              <p className="text-gray-600 mb-6">
                Comprehensive evaluation of your organization's AI readiness, data infrastructure, and technology capabilities for successful AI adoption.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <div className="w-4 h-4 flex items-center justify-center text-purple-600 mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-sm text-gray-700">Data quality assessment</span>
                </li>
                <li className="flex items-start">
                  <div className="w-4 h-4 flex items-center justify-center text-purple-600 mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-sm text-gray-700">Infrastructure evaluation</span>
                </li>
                <li className="flex items-start">
                  <div className="w-4 h-4 flex items-center justify-center text-purple-600 mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-sm text-gray-700">Skills gap analysis</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-8">
              <div className="w-16 h-16 bg-orange-50 rounded-lg flex items-center justify-center mb-6">
                <div className="w-8 h-8 flex items-center justify-center text-orange-600">
                  <i className="ri-brain-line text-3xl"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-4">Machine Learning Solutions</h3>
              <p className="text-gray-600 mb-6">
                Custom machine learning model development, training, and deployment for predictive analytics, automation, and intelligent decision-making.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <div className="w-4 h-4 flex items-center justify-center text-orange-600 mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-sm text-gray-700">Predictive modeling</span>
                </li>
                <li className="flex items-start">
                  <div className="w-4 h-4 flex items-center justify-center text-orange-600 mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-sm text-gray-700">Natural language processing</span>
                </li>
                <li className="flex items-start">
                  <div className="w-4 h-4 flex items-center justify-center text-orange-600 mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-sm text-gray-700">Computer vision systems</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* AI Readiness Assessment Tools */}
      <section className="py-16" id="assessment-tools">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">AI Readiness Assessment Tools</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Evaluate your organization's AI readiness with our comprehensive assessment tools and get actionable insights to accelerate your AI transformation journey.
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-8">
              <h3 className="text-2xl font-bold mb-6">AI Maturity Assessment</h3>
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">Data Infrastructure Readiness</label>
                  <input 
                    type="range" 
                    min="1" 
                    max="10" 
                    value={dataReadiness}
                    onChange={(e) => setDataReadiness(Number(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>Basic</span>
                    <span>Advanced</span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">AI Skills & Expertise</label>
                  <input 
                    type="range" 
                    min="1" 
                    max="10" 
                    value={aiSkills}
                    onChange={(e) => setAiSkills(Number(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>Limited</span>
                    <span>Expert</span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">Technology Infrastructure</label>
                  <input 
                    type="range" 
                    min="1" 
                    max="10" 
                    value={techInfrastructure}
                    onChange={(e) => setTechInfrastructure(Number(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>Legacy</span>
                    <span>Modern</span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">Organizational Readiness</label>
                  <input 
                    type="range" 
                    min="1" 
                    max="10" 
                    value={orgReadiness}
                    onChange={(e) => setOrgReadiness(Number(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>Resistant</span>
                    <span>Embracing</span>
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">AI Maturity Score:</span>
                    <span className="text-2xl font-bold text-primary">{maturityScore}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-primary h-2 rounded-full transition-all duration-300"
                      style={{ width: `${maturityScore}%` }}
                    ></div>
                  </div>
                  <p className="text-sm text-gray-600 mt-2">{getMaturityRecommendation()}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-8">
              <h3 className="text-2xl font-bold mb-6">AI Implementation Timeline</h3>
              <div className="h-64 flex items-center justify-center bg-gray-50 rounded-lg mb-6">
                <div className="text-center text-gray-400">
                  <i className="ri-bar-chart-line text-5xl mb-2"></i>
                  <p className="text-sm">Timeline Visualization</p>
                </div>
              </div>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-blue-500 rounded-full mr-3"></div>
                    <span className="text-sm font-medium">Strategy & Planning</span>
                  </div>
                  <span className="text-sm text-gray-600">Months 1-2</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                    <span className="text-sm font-medium">Pilot Implementation</span>
                  </div>
                  <span className="text-sm text-gray-600">Months 3-6</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-orange-500 rounded-full mr-3"></div>
                    <span className="text-sm font-medium">Full Deployment</span>
                  </div>
                  <span className="text-sm text-gray-600">Months 7-12</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-purple-500 rounded-full mr-3"></div>
                    <span className="text-sm font-medium">Optimization</span>
                  </div>
                  <span className="text-sm text-gray-600">Ongoing</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AI Governance Framework */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">AI Governance Framework</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Establish responsible AI practices with our comprehensive governance framework ensuring ethical, compliant, and sustainable AI implementations.
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="space-y-8">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <div className="w-6 h-6 flex items-center justify-center text-primary">
                      <i className="ri-shield-check-line text-xl"></i>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">AI Ethics & Compliance</h3>
                    <p className="text-gray-600">
                      Establish ethical AI principles, bias detection, and regulatory compliance frameworks to ensure responsible AI deployment.
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <div className="w-6 h-6 flex items-center justify-center text-green-600">
                      <i className="ri-file-shield-line text-xl"></i>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Risk Management</h3>
                    <p className="text-gray-600">
                      Comprehensive AI risk assessment, mitigation strategies, and continuous monitoring to minimize potential negative impacts.
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <div className="w-6 h-6 flex items-center justify-center text-purple-600">
                      <i className="ri-team-line text-xl"></i>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Governance Structure</h3>
                    <p className="text-gray-600">
                      Define AI governance committees, decision-making processes, and accountability frameworks for effective AI oversight.
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <div className="w-6 h-6 flex items-center justify-center text-orange-600">
                      <i className="ri-eye-line text-xl"></i>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Transparency & Explainability</h3>
                    <p className="text-gray-600">
                      Implement AI transparency measures, model explainability, and clear documentation for stakeholder understanding.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-8">
                <h3 className="text-2xl font-bold mb-6 text-center">AI Governance Maturity</h3>
                <div className="relative w-64 h-64 mx-auto">
                  <svg viewBox="0 0 100 100" className="transform -rotate-90">
                    <circle cx="50" cy="50" r="40" fill="none" stroke="#e5e7eb" strokeWidth="8" />
                    <circle cx="50" cy="50" r="40" fill="none" stroke="#0066CC" strokeWidth="8" strokeDasharray="251.2" strokeDashoffset="62.8" strokeLinecap="round" />
                    <circle cx="50" cy="50" r="40" fill="none" stroke="#10b981" strokeWidth="8" strokeDasharray="251.2" strokeDashoffset="125.6" strokeLinecap="round" />
                    <circle cx="50" cy="50" r="40" fill="none" stroke="#f59e0b" strokeWidth="8" strokeDasharray="251.2" strokeDashoffset="188.4" strokeLinecap="round" />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      <div className="text-3xl font-bold text-gray-900">75%</div>
                      <div className="text-sm text-gray-500">Maturity</div>
                    </div>
                  </div>
                </div>
                <div className="mt-6 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
                      <span className="text-sm">Ethics & Compliance</span>
                    </div>
                    <span className="text-sm font-medium">80%</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                      <span className="text-sm">Risk Management</span>
                    </div>
                    <span className="text-sm font-medium">70%</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="w-3 h-3 bg-orange-500 rounded-full mr-2"></div>
                      <span className="text-sm">Transparency</span>
                    </div>
                    <span className="text-sm font-medium">75%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Request AI Consultation Form */}
      <section className="py-16 bg-white" id="consultation-form">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Request AI Consultation</h2>
              <p className="text-gray-600">
                Get expert AI consulting advice tailored to your business needs. Our AI specialists will assess your requirements and recommend appropriate solutions.
              </p>
            </div>
            <form 
              className="bg-white rounded-lg shadow-sm border border-gray-100 p-8 space-y-6" 
              onSubmit={handleFormSubmit}
              data-readdy-form
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="company_name" className="block text-sm font-medium text-gray-700 mb-2">
                    Company Name *
                  </label>
                  <input
                    id="company_name"
                    name="company_name"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                    placeholder="Enter your company name"
                  />
                </div>
                <div>
                  <label htmlFor="industry" className="block text-sm font-medium text-gray-700 mb-2">
                    Industry *
                  </label>
                  <select
                    id="industry"
                    name="industry"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                  >
                    <option value="">Select your industry</option>
                    <option value="technology">Technology</option>
                    <option value="finance">Finance & Banking</option>
                    <option value="healthcare">Healthcare</option>
                    <option value="manufacturing">Manufacturing</option>
                    <option value="retail">Retail & E-commerce</option>
                    <option value="education">Education</option>
                    <option value="government">Government</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="contact_name" className="block text-sm font-medium text-gray-700 mb-2">
                    Contact Name *
                  </label>
                  <input
                    id="contact_name"
                    name="contact_name"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                    placeholder="Enter your full name"
                  />
                </div>
                <div>
                  <label htmlFor="job_title" className="block text-sm font-medium text-gray-700 mb-2">
                    Job Title *
                  </label>
                  <input
                    id="job_title"
                    name="job_title"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                    placeholder="Enter your job title"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Business Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                    placeholder="Enter your business email"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                    placeholder="Enter your phone number"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="company_size" className="block text-sm font-medium text-gray-700 mb-2">
                    Company Size *
                  </label>
                  <select
                    id="company_size"
                    name="company_size"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                  >
                    <option value="">Select company size</option>
                    <option value="1-10">1-10 employees</option>
                    <option value="11-50">11-50 employees</option>
                    <option value="51-200">51-200 employees</option>
                    <option value="201-500">201-500 employees</option>
                    <option value="501-1000">501-1000 employees</option>
                    <option value="1000+">1000+ employees</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="ai_budget" className="block text-sm font-medium text-gray-700 mb-2">
                    AI Budget Range
                  </label>
                  <select
                    id="ai_budget"
                    name="ai_budget"
                    className="w-full px-4 py-3 border border-gray-300 rounded focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                  >
                    <option value="">Select budget range</option>
                    <option value="under-50k">Under $50K</option>
                    <option value="50k-100k">$50K - $100K</option>
                    <option value="100k-250k">$100K - $250K</option>
                    <option value="250k-500k">$250K - $500K</option>
                    <option value="500k-1m">$500K - $1M</option>
                    <option value="over-1m">Over $1M</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  AI Use Cases (Select all that apply) *
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <label className="flex items-center cursor-pointer">
                    <input type="checkbox" name="use_cases" value="Predictive Analytics" className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary cursor-pointer" />
                    <span className="ml-3 text-sm text-gray-700">Predictive Analytics</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input type="checkbox" name="use_cases" value="Process Automation" className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary cursor-pointer" />
                    <span className="ml-3 text-sm text-gray-700">Process Automation</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input type="checkbox" name="use_cases" value="Natural Language Processing" className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary cursor-pointer" />
                    <span className="ml-3 text-sm text-gray-700">Natural Language Processing</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input type="checkbox" name="use_cases" value="Computer Vision" className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary cursor-pointer" />
                    <span className="ml-3 text-sm text-gray-700">Computer Vision</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input type="checkbox" name="use_cases" value="Recommendation Systems" className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary cursor-pointer" />
                    <span className="ml-3 text-sm text-gray-700">Recommendation Systems</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input type="checkbox" name="use_cases" value="Fraud Detection" className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary cursor-pointer" />
                    <span className="ml-3 text-sm text-gray-700">Fraud Detection</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input type="checkbox" name="use_cases" value="Chatbots & Virtual Assistants" className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary cursor-pointer" />
                    <span className="ml-3 text-sm text-gray-700">Chatbots & Virtual Assistants</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input type="checkbox" name="use_cases" value="Business Optimization" className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary cursor-pointer" />
                    <span className="ml-3 text-sm text-gray-700">Business Optimization</span>
                  </label>
                </div>
              </div>

              <div>
                <label htmlFor="ai_requirements" className="block text-sm font-medium text-gray-700 mb-2">
                  AI Requirements & Goals
                </label>
                <textarea
                  id="ai_requirements"
                  name="ai_requirements"
                  rows={4}
                  maxLength={500}
                  onChange={(e) => setCharCount(e.target.value.length)}
                  className="w-full px-4 py-3 border border-gray-300 rounded focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                  placeholder="Please describe your current AI initiatives, specific challenges you're facing, and your AI objectives..."
                ></textarea>
                <div className="mt-2 text-sm text-gray-500 flex items-center">
                  <div className="w-4 h-4 flex items-center justify-center mr-1">
                    <i className="ri-information-line"></i>
                  </div>
                  <span>{charCount}/500 characters</span>
                </div>
              </div>

              <div>
                <label htmlFor="preferred_timeline" className="block text-sm font-medium text-gray-700 mb-2">
                  Preferred Timeline
                </label>
                <select
                  id="preferred_timeline"
                  name="preferred_timeline"
                  className="w-full px-4 py-3 border border-gray-300 rounded focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                >
                  <option value="">Select timeline</option>
                  <option value="urgent">Urgent (within 1 month)</option>
                  <option value="immediate">Immediate (1-3 months)</option>
                  <option value="quarter">This quarter (3-6 months)</option>
                  <option value="half-year">Next 6 months</option>
                  <option value="planning">Planning phase (6+ months)</option>
                </select>
              </div>

              <div className="flex items-start">
                <input
                  type="checkbox"
                  id="privacy_consent"
                  name="privacy_consent"
                  required
                  className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary mt-1 cursor-pointer"
                />
                <label htmlFor="privacy_consent" className="ml-3 text-sm text-gray-600">
                  I agree to be contacted by Thyamis regarding this AI consultation request and consent to the processing of my data according to the Privacy Policy *
                </label>
              </div>

              <div className="flex justify-center">
                <button
                  type="submit"
                  className="bg-primary text-white px-8 py-3 rounded-lg whitespace-nowrap font-medium hover:bg-primary/90 transition-colors inline-flex items-center cursor-pointer"
                >
                  <div className="w-5 h-5 flex items-center justify-center mr-2">
                    <i className="ri-send-plane-line"></i>
                  </div>
                  Submit AI Consultation Request
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 relative bg-gradient-to-r from-blue-900 via-blue-800 to-blue-900">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://readdy.ai/api/search-image?query=futuristic%20AI%20technology%20neural%20network%20with%20glowing%20blue%20circuits%20and%20digital%20data%20streams%20in%20modern%20tech%20environment%20with%20dark%20blue%20tones%20and%20high%20tech%20atmosphere&width=1920&height=400&seq=aicta1&orientation=landscape)',
          }}
        ></div>
        <div className="absolute inset-0 bg-black/50"></div>
        <div className="container mx-auto px-4 relative">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-6">Transform Your Business with AI Today</h2>
            <p className="text-gray-200 mb-8">
              Don't let your competitors gain the AI advantage. Our expert consultants are ready to assess your AI opportunities and implement solutions that drive real business results.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="tel:+31610749083"
                className="bg-transparent border border-white text-white px-6 py-3 rounded-lg whitespace-nowrap font-medium inline-flex items-center hover:bg-white/10 transition-colors cursor-pointer"
              >
                <div className="w-5 h-5 flex items-center justify-center mr-2">
                  <i className="ri-phone-line"></i>
                </div>
                Call AI Experts: +31 (0)616300011
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowSuccessModal(false)}></div>
          <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 relative">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <div className="w-8 h-8 flex items-center justify-center text-green-500">
                  <i className="ri-check-line text-3xl"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-2">Request Submitted!</h3>
              <p className="text-gray-600 mb-6">
                Thank you for your AI consultation request. Our AI specialists will contact you within 24 hours to discuss your AI transformation needs.
              </p>
              <button
                onClick={() => setShowSuccessModal(false)}
                className="bg-primary text-white px-6 py-2 rounded-lg whitespace-nowrap font-medium hover:bg-primary/90 transition-colors cursor-pointer"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
      <ChatWidget />
    </div>
  );
};

export default AIConsultingPage;